import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'Store/index';
import { Input } from 'App/shadcn/ui/input';
import { Badge } from 'App/shadcn/ui/badge';
import DateTimePicker from 'App/shadcn/components/DateTimePicker';
import EntityPicker, { type EntityOption } from 'App/shadcn/components/EntityPicker';
import ClientResource from 'App/resources/Client';
import ContactResource from 'Resources/Contact';
import UserResource from 'App/resources/User';
import ProjectResource from 'App/resources/Project';
import AppService from 'App/legacy/AppService';

export type EntityKind =
	| 'client'
	| 'contact'
	| 'user'
	| 'project'
	| 'activityType'
	| 'appointmentType'
	| 'stage'
	| 'product'
	| 'category';

export const ENTITY_FIELDS: Record<string, EntityKind> = {
	client: 'client',
	account: 'client',
	contact: 'contact',
	contacts: 'contact',
	user: 'user',
	users: 'user',
	assigneduser: 'user',
	regby: 'user',
	project: 'project',
	projects: 'project',
	activitytype: 'activityType',
	appointmenttype: 'appointmentType',
	stage: 'stage',
	product: 'product',
	products: 'product',
	category: 'category',
	categories: 'category'
};

export const MULTI_FIELDS = new Set(['contacts', 'users', 'projects', 'products', 'categories']);

/** Sync-resolved ref fields read their option list from AppService —
 *  no network call required. Returns null for fields backed by a remote
 *  search (client / contact / project). */
function getSyncOptions(kind: EntityKind): EntityOption[] | null {
	switch (kind) {
		case 'activityType':
			return AppService.getActivityTypes('activity') as EntityOption[];
		// Appointment types are activity types scoped to 'appointment'. This used to call a
		// getAppointmentTypes() that exists on no AppService, Angular or standalone, so the
		// optional call always yielded [] and appointment-type fields never had options.
		case 'appointmentType':
			return AppService.getActivityTypes('appointment') as EntityOption[];
		case 'stage': {
			const all: EntityOption[] = [];
			for (const t of ['opportunity', 'order', 'lost'] as const) {
				all.push(...(AppService.getStages(t) as unknown as EntityOption[]));
			}
			return all;
		}
		case 'product':
			return AppService.getProducts() as unknown as EntityOption[];
		case 'category': {
			const all: EntityOption[] = [];
			for (const t of ['account', 'contact', 'activity']) {
				all.push(...(AppService.getCategories(t) as unknown as EntityOption[]));
			}
			return all;
		}
		default:
			return null;
	}
}

export function isDateString(value: unknown): boolean {
	if (typeof value !== 'string') return false;
	return /^\d{4}-\d{2}-\d{2}/.test(value);
}

export function formatFieldName(key: string): string {
	return key
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]/g, ' ')
		.replace(/^./, s => s.toUpperCase())
		.trim();
}

export function isNestedObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function formatNestedValue(value: Record<string, unknown>): string {
	if ('name' in value && value.name) return String(value.name);
	if ('id' in value) return `#${value.id}`;
	return JSON.stringify(value);
}

/** True for an array whose items are plain objects (line items like orderRow),
 *  excluding entity-ref fields (users / contacts) which render as pickers. */
function isObjectArray(key: string, value: unknown): value is Record<string, unknown>[] {
	if (ENTITY_FIELDS[key.toLowerCase()]) return false;
	return Array.isArray(value) && value.length > 0 && value.every(isNestedObject);
}

/** Drop a flat `<ref>Id` key when the nested `<ref>` object is also present —
 *  the object already shows the name (same as how client / contact / user are
 *  shown). e.g. an order row with {product:{id,name}, productId} hides productId. */
function visibleObjectEntries(obj: Record<string, unknown>): [string, unknown][] {
	return Object.entries(obj).filter(([k]) => {
		if (/Id$/.test(k)) {
			const base = k.slice(0, -2);
			if (isNestedObject(obj[base])) return false;
		}
		return true;
	});
}

/** Render a single value to a display string: nested ref → its name, number →
 *  localized, array → comma-joined (refs by name). */
function formatScalarValue(value: unknown): string {
	if (value === null || value === undefined || value === '') return '—';
	if (isNestedObject(value)) return formatNestedValue(value);
	if (Array.isArray(value)) {
		return value.map(v => (isNestedObject(v) ? formatNestedValue(v) : String(v))).join(', ');
	}
	if (typeof value === 'number') return value.toLocaleString();
	return String(value);
}

function extractId(obj: unknown): number | string | null {
	if (!isNestedObject(obj)) return null;
	const id = obj.id ?? obj.userId ?? obj.contactId ?? obj.clientId;
	return id === undefined || id === null ? null : (id as number | string);
}

function extractName(obj: unknown): string {
	if (!isNestedObject(obj)) return '';
	if (typeof obj.name === 'string' && obj.name) return obj.name;
	const fn = typeof obj.firstName === 'string' ? obj.firstName : '';
	const ln = typeof obj.lastName === 'string' ? obj.lastName : '';
	const full = `${fn} ${ln}`.trim();
	if (full) return full;
	if (typeof obj.email === 'string') return obj.email;
	return '';
}

export function toOption(obj: unknown): EntityOption | null {
	const id = extractId(obj);
	if (id === null) return null;
	return { id, name: extractName(obj) };
}

type AnyResource = {
	find: (params: Record<string, unknown>) => Promise<{ data: Array<Record<string, unknown>> }>;
	get?: (id: number | string) => Promise<{ data: Record<string, unknown> }>;
};

function getResource(kind: EntityKind): AnyResource | null {
	if (kind === 'client') return ClientResource as unknown as AnyResource;
	if (kind === 'contact') return ContactResource as unknown as AnyResource;
	if (kind === 'user') return UserResource as unknown as AnyResource;
	if (kind === 'project') return ProjectResource as unknown as AnyResource;
	return null;
}

async function searchCrmEntity(kind: EntityKind, term: string): Promise<EntityOption[]> {
	const resource = getResource(kind);
	if (!resource) return [];
	try {
		const trimmed = term.trim();
		const filters: Record<string, unknown> = { limit: 20, sort: [{ a: 'name', s: 'A' }] };
		if (trimmed) filters.q = [{ a: 'name', c: 'src', v: trimmed }];
		const res = await resource.find(filters);
		const rows = Array.isArray(res?.data) ? res.data : [];
		return rows.map(toOption).filter((v): v is EntityOption => Boolean(v));
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[FieldGrid] searchCrmEntity failed', { kind, term, err });
		return [];
	}
}

async function fetchCrmEntityById(kind: EntityKind, id: number | string): Promise<EntityOption | null> {
	const resource = getResource(kind);
	if (!resource?.get) return null;
	try {
		const res = await resource.get(id);
		return toOption(res?.data ?? null);
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[FieldGrid] fetchCrmEntityById failed', { kind, id, err });
		return null;
	}
}

type Props = {
	/** Field-name → current value. Field names are case-insensitive against
	 *  `ENTITY_FIELDS` for ref-picker detection. */
	data: Record<string, unknown>;
	onChange: (next: Record<string, unknown>) => void;
	disabled?: boolean;
	/** When set, the matching row renders as a non-editable badge (used for
	 *  the `id` field on an Update proposal). */
	readOnlyKeys?: ReadonlySet<string>;
};

/**
 * The shared editor surface for WriteCard / MultiActionWriteCard. Renders a
 * dl-grid of fields, picks the right editor per field type (EntityPicker for
 * refs, DateTimePicker for dates, number/text for primitives), and hydrates
 * ref names so a `{id: 1}` shows "Anna Svensson" without a roundtrip.
 */
const FieldGrid = ({ data, onChange, disabled = false, readOnlyKeys }: Props) => {
	const activeUsers = useSelector(
		(state: RootState) =>
			(
				state as unknown as {
					App?: {
						userMap?: Record<
							string,
							Array<{ id: number; name?: string; firstName?: string; lastName?: string; email?: string }>
						>;
					};
				}
			).App?.userMap?.active ?? []
	);
	const userOptions = useMemo<EntityOption[]>(
		() =>
			activeUsers.map(u => ({
				id: u.id,
				name: u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || `#${u.id}`
			})),
		[activeUsers]
	);
	const userById = useMemo(() => new Map(userOptions.map(u => [u.id, u])), [userOptions]);

	const searchEntity = useCallback(
		async (kind: EntityKind, term: string): Promise<EntityOption[]> => {
			if (kind === 'user') {
				const q = term.trim().toLowerCase();
				if (!q) return userOptions.slice(0, 50);
				return userOptions.filter(u => u.name.toLowerCase().includes(q)).slice(0, 50);
			}
			const sync = getSyncOptions(kind);
			if (sync) {
				const q = term.trim().toLowerCase();
				if (!q) return sync.slice(0, 50);
				return sync.filter(o => o.name?.toLowerCase().includes(q)).slice(0, 50);
			}
			return searchCrmEntity(kind, term);
		},
		[userOptions]
	);

	const fetchEntityById = useCallback(
		async (kind: EntityKind, id: number | string): Promise<EntityOption | null> => {
			if (kind === 'user') return userById.get(id) ?? { id, name: `#${id}` };
			const sync = getSyncOptions(kind);
			if (sync) return sync.find(o => o.id === id) ?? { id, name: `#${id}` };
			return fetchCrmEntityById(kind, id);
		},
		[userById]
	);

	const [displayCache, setDisplayCache] = useState<Record<string, EntityOption[]>>(() => {
		const seed: Record<string, EntityOption[]> = {};
		const hydrateOne = (kind: EntityKind, raw: unknown): EntityOption | null => {
			// Accept primitive ids (multi-action sends bare numbers) by wrapping
			// them into the option shape — the sync lookup below resolves the name.
			let opt: EntityOption | null = null;
			if (typeof raw === 'number' || typeof raw === 'string') {
				opt = { id: raw, name: '' };
			} else {
				opt = toOption(raw);
			}
			if (!opt) return null;
			if (opt.name) return opt;
			if (kind === 'user') {
				const cached = userById.get(opt.id);
				if (cached?.name) return cached;
			}
			const sync = getSyncOptions(kind);
			if (sync) {
				const found = sync.find(o => o.id === opt!.id);
				if (found?.name) return found;
			}
			return opt;
		};
		for (const [key, value] of Object.entries(data)) {
			const kind = ENTITY_FIELDS[key.toLowerCase()];
			if (!kind) continue;
			if (Array.isArray(value)) {
				seed[key] = value.map(v => hydrateOne(kind, v)).filter((v): v is EntityOption => Boolean(v));
			} else {
				const opt = hydrateOne(kind, value);
				if (opt) seed[key] = [opt];
			}
		}
		return seed;
	});
	const hydratedRef = useRef(false);

	useEffect(() => {
		if (hydratedRef.current && userOptions.length === 0) return;
		hydratedRef.current = true;
		(async () => {
			const updates: Array<[string, EntityOption[]]> = [];
			for (const [key, value] of Object.entries(data)) {
				const kind = ENTITY_FIELDS[key.toLowerCase()];
				if (!kind) continue;
				const raws = Array.isArray(value) ? value : value ? [value] : [];
				const hydrated = await Promise.all(
					raws.map(async raw => {
						const existing = toOption(raw);
						// Accept bare-id refs from the registry-shape (multi-action sends
						// primitive ids). Wrap them so the picker can hydrate.
						if (!existing && (typeof raw === 'number' || typeof raw === 'string')) {
							const fetched = await fetchEntityById(kind, raw);
							return fetched ?? { id: raw, name: `#${raw}` };
						}
						if (!existing) return null;
						if (existing.name) return existing;
						const fetched = await fetchEntityById(kind, existing.id);
						return fetched ?? existing;
					})
				);
				const cleaned = hydrated.filter((v): v is EntityOption => Boolean(v));
				if (cleaned.length) updates.push([key, cleaned]);
			}
			if (updates.length) {
				setDisplayCache(prev => {
					const next = { ...prev };
					for (const [k, v] of updates) next[k] = v;
					return next;
				});
			}
		})();
	}, [data, userOptions.length, fetchEntityById]);

	const handleFieldChange = useCallback(
		(key: string, value: unknown) => {
			onChange({ ...data, [key]: value });
		},
		[data, onChange]
	);

	const handleEntityPick = useCallback(
		(key: string, next: EntityOption | EntityOption[] | null) => {
			if (Array.isArray(next)) {
				setDisplayCache(prev => ({ ...prev, [key]: next }));
				handleFieldChange(
					key,
					next.map(({ id }) => ({ id }))
				);
			} else if (next) {
				setDisplayCache(prev => ({ ...prev, [key]: [next] }));
				handleFieldChange(key, { id: next.id });
			} else {
				setDisplayCache(prev => ({ ...prev, [key]: [] }));
				handleFieldChange(key, null);
			}
		},
		[handleFieldChange]
	);

	return (
		<dl className="pr-wcard-fields">
			{Object.entries(data)
				// Object-arrays (line items like orderRow) render as a titled
				// sub-group; push them below the scalar/ref fields so summary
				// fields (description, dates, refs) read first. Stable sort keeps
				// the relative order of everything else.
				.sort(([ka, va], [kb, vb]) => (isObjectArray(ka, va) ? 1 : 0) - (isObjectArray(kb, vb) ? 1 : 0))
				.map(([key, value]) => {
					if (readOnlyKeys?.has(key)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<Badge variant="secondary" className="text-body-sm">
										{`#${String(value)}`}
									</Badge>
								</dd>
							</React.Fragment>
						);
					}

					const entityKind = ENTITY_FIELDS[key.toLowerCase()];
					if (entityKind) {
						const isMulti = MULTI_FIELDS.has(key.toLowerCase()) || Array.isArray(value);
						const cachedOptions = displayCache[key] ?? [];
						if (isMulti) {
							return (
								<React.Fragment key={key}>
									<dt>{formatFieldName(key)}</dt>
									<dd>
										<EntityPicker
											ghost
											multiple
											value={cachedOptions}
											onChange={next => handleEntityPick(key, next)}
											search={term => searchEntity(entityKind, term)}
											placeholder={`Search ${formatFieldName(key).toLowerCase()}…`}
											disabled={disabled}
										/>
									</dd>
								</React.Fragment>
							);
						}
						const current = cachedOptions[0] ?? null;
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<EntityPicker
										ghost
										value={current}
										onChange={next => handleEntityPick(key, next)}
										search={term => searchEntity(entityKind, term)}
										placeholder={`Search ${formatFieldName(key).toLowerCase()}…`}
										disabled={disabled}
									/>
								</dd>
							</React.Fragment>
						);
					}

					if (isNestedObject(value)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<Badge variant="outline" className="text-body-sm">
										{formatNestedValue(value)}
									</Badge>
								</dd>
							</React.Fragment>
						);
					}

					// Array of objects (line items like orderRow) — render each item as
					// an indented, titled, separated sub-grid of its own fields, reusing
					// formatFieldName so labels match the rest of the card. Generic: any
					// non-entity object-array gets this treatment.
					if (isObjectArray(key, value)) {
						return (
							<div key={key} className="pr-objarray">
								<div className="pr-objarray-title">{formatFieldName(key)}</div>
								{value.map((item, i) => (
									<div key={i} className="pr-objarray-item">
										{value.length > 1 && <div className="pr-objarray-num">{`#${i + 1}`}</div>}
										<dl className="pr-objarray-fields">
											{visibleObjectEntries(item).map(([rk, rv]) => (
												<React.Fragment key={rk}>
													<dt>{formatFieldName(rk)}</dt>
													<dd>{formatScalarValue(rv)}</dd>
												</React.Fragment>
											))}
										</dl>
									</div>
								))}
							</div>
						);
					}

					if (Array.isArray(value)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<span className="text-body-sm" style={{ color: 'var(--pr-ink)' }}>
										{value.length ? formatScalarValue(value) : '—'}
									</span>
								</dd>
							</React.Fragment>
						);
					}

					const isDate = isDateString(value);
					const isNumber = typeof value === 'number';

					if (isDate) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<DateTimePicker
										ghost
										value={String(value)}
										onChange={next => handleFieldChange(key, next)}
										disabled={disabled}
									/>
								</dd>
							</React.Fragment>
						);
					}

					return (
						<React.Fragment key={key}>
							<dt>{formatFieldName(key)}</dt>
							<dd>
								<Input
									className="h-7 text-body-sm border-transparent bg-transparent px-1.5 shadow-none hover:border-input hover:bg-background focus:border-input focus:bg-background"
									type={isNumber ? 'number' : 'text'}
									value={String(value ?? '')}
									disabled={disabled}
									onChange={e => {
										const newValue = isNumber ? Number(e.target.value) : e.target.value;
										handleFieldChange(key, newValue);
									}}
								/>
							</dd>
						</React.Fragment>
					);
				})}
		</dl>
	);
};

export default FieldGrid;
